import React from 'react';
import { Input, Button, Tooltip } from '@heroui/react';
import {
  FilterSelect,
  FilterOption,
  ModelSearchSelect,
  ResponsiveTooltip,
} from '../../../../components/ui';
import { useTranslations } from '../../../../contexts/translation.context';
import {
  SearchInputIc,
  AccountIcon,
  WidgetIc,
  DangerIc,
  GlobalIc,
  ResetFiltersIc,
} from '../../../../components/icons';
import { getSortOptions, getOrderStatuses, getCampaignTypes, getOrderSources } from '../constants';
import { FiltersProps, OrdersFilters } from '../types';

const FiltersSection: React.FC<FiltersProps> = ({
  filters,
  localSearch,
  localSubsForm,
  localIncome,
  availableModels,
  modelsLoading,
  sortDirection,
  hasActiveFilters,
  onSearchChange,
  onSubsFormChange,
  onIncomeChange,
  onFilterChange,
  onModelSearchChange,
  onSortDirectionChange,
  onResetFilters,
}) => {
  const { t } = useTranslations();
  return (
    <div className="px-4 pb-6 md:px-6 md:pb-6 flex items-center gap-4 flex-wrap justify-between w-full">
      <div className="flex items-center gap-4 md:flex-wrap md:justify-start max-md:h-10 [&>*]:shrink-0 max-md:overflow-x-auto max-md:px-4 max-md:-mx-4 scroller-none">
        <Input
          type="text"
          placeholder={'ID'}
          size="sm"
          value={localSearch}
          onChange={(e) => onSearchChange(e.target.value)}
          endContent={<SearchInputIc />}
          classNames={{
            mainWrapper: 'w-[17.0625rem]',
            base: 'w-[17.0625rem]',
            input: 'text-sm',
            inputWrapper: 'rounded-lg h-9 w-[17.0625rem]',
          }}
        />

        <FilterSelect
          placeholder={t('orders.filters.sort.placeholder')}
          options={getSortOptions(t)}
          selectedKey={filters.sort}
          variant="campaign"
          onSelectionChange={(value) => onFilterChange('sort', value)}
          startContent={
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
              <path
                fill="none"
                stroke="#A8A8B0"
                strokeLinecap="round"
                strokeWidth="1.5"
                d="M22 7H2m17 5H5m11 5H8"
              />
            </svg>
          }
          autoWidth={true}
          showPlaceholderWithValue={true}
          sortDirection={filters.sort !== 'all' ? sortDirection : null}
          onSortDirectionChange={onSortDirectionChange}
        />

        <ModelSearchSelect
          placeholder={t('orders.filters.model.placeholder')}
          options={[
            ...availableModels.map(
              (model) =>
                ({
                  key: model.id,
                  avatar: model.avatar,
                  username: model.username,
                  name: model.name,
                  label: `${model.name} (@${model.username})`,
                }) as FilterOption
            ),
          ]}
          selectedKey={filters.models.length > 0 ? filters.models[0] : 'all'}
          onSelectionChange={(value) => {
            const newModels = value === 'all' ? [] : [value];
            onFilterChange('models', newModels);
          }}
          onSearchChange={onModelSearchChange}
          isLoading={modelsLoading}
          startContent={<AccountIcon className="text-neutral-8 stroke-neutral-8" size={20} />}
          variant="campaign"
          autoWidth={true}
          t={t}
        />

        <FilterSelect
          placeholder={t('orders.filters.campaignType.placeholder')}
          options={getCampaignTypes(t)}
          selectedKey={filters.campaignType}
          variant="campaign"
          onSelectionChange={(value) => onFilterChange('campaignType', value)}
          startContent={<WidgetIc />}
          autoWidth={true}
          showPlaceholderWithValue={true}
        />

        <FilterSelect
          placeholder={t('orders.filters.status.placeholder')}
          options={getOrderStatuses(t)}
          selectedKey={filters.status}
          variant="campaign"
          onSelectionChange={(value) => onFilterChange('status', value)}
          startContent={<DangerIc size={20} />}
          autoWidth={true}
          showPlaceholderWithValue={true}
        />

        <FilterSelect
          placeholder={t('orders.filters.sources.placeholder')}
          options={getOrderSources(t)}
          selectedKey={filters.source}
          variant="campaign"
          onSelectionChange={(value) => onFilterChange('source', value)}
          // startContent={<GlobalIc/>}
          autoWidth={true}
          showPlaceholderWithValue={true}
        />
        <Input
          type="number"
          placeholder="0"
          startContent={
            <span className="text-auth-muted text-sm whitespace-nowrap">
              {t('orders.filters.fansFrom')}:
            </span>
          }
          size="sm"
          value={localSubsForm}
          onValueChange={onSubsFormChange}
          classNames={{
            mainWrapper: 'w-fit max-w-[12.5rem]',
            base: 'w-fit max-w-[12.5rem]',
            input: 'text-sm w-[3.75rem]',
            inputWrapper: 'rounded-lg h-9',
          }}
        />
        <Input
          type="number"
          placeholder="0"
          startContent={
            <span className="text-auth-muted text-sm whitespace-nowrap">
              {t('orders.filters.revenueFrom')}:
            </span>
          }
          size="sm"
          value={localIncome}
          onValueChange={onIncomeChange}
          classNames={{
            mainWrapper: 'w-fit max-w-[12.5rem]',
            base: 'w-fit max-w-[12.5rem]',
            input: 'text-sm w-[3.75rem]',
            inputWrapper: 'rounded-lg h-9',
          }}
        />
        {!hasActiveFilters && <div className="shrink-0 w-10 h-2 md:hidden"></div>}
        {hasActiveFilters && (
          <Tooltip content={t('orders.filters.resetTooltip')} placement="top">
            <Button
              //@ts-ignore
              size="xs"
              className="h-9 rounded-2 !w-9 p-0 bg-auth-surface shrink-0"
              onPress={onResetFilters}
            >
              <ResetFiltersIc />
            </Button>
          </Tooltip>
        )}
      </div>
    </div>
  );
};

export default FiltersSection;
